import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { X } from 'lucide-react';

// Styled Components - ChatGPT-style modal
const ModalOverlay = styled.div<{ $isOpen: boolean; $zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(1px);
  z-index: ${props => props.$zIndex || 1000};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
`;

const ModalContainer = styled.div<{ $isOpen: boolean; $size?: string; $zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: ${props => (props.$zIndex || 1000) + 1};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  pointer-events: none;
  
  @media (max-width: 767px) {
    padding: 6px 12px;
    align-items: stretch;
  }
  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const ModalDialog = styled.div<{ $isOpen: boolean; $size?: string }>`
  width: min(${props => {
    if (props.$size === 'sm') return '400px';
    if (props.$size === 'md') return '750px';
    return '920px';
  }}, calc(100vw - 32px));
  max-height: min(80vh, calc(100vh - 32px));
  background: ${props => props.theme.background};
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  display: flex;
  flex-direction: column;
  display: flex;
  flex-direction: column;
  transform: ${props => props.$isOpen ? 'scale(1)' : 'scale(0.98)'};
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: ${props => props.$size === 'sm' || props.$size === 'md' ? 'visible' : 'hidden'};
  pointer-events: auto;
  margin: 0;

  @media (max-width: 767px) {
    max-height: calc(100vh - 12px);
    min-height: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  
  @media (min-width: 640px) {
    padding: 12px 24px;
  }
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 12px;
  color: ${props => props.theme.mutedForeground};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.theme.muted};
    color: ${props => props.theme.foreground};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${props => props.theme.primary};
  }
`;

const ModalBody = styled.div<{ $size?: string; $hasSidebar?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  flex: 1;
  min-height: 0;
  overflow: ${props => props.$size === 'sm' || props.$size === 'md' ? 'hidden' : 'auto'};
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => {
      if (props.$size === 'sm' || props.$size === 'md') return '1fr';
      /* lg/xl: only use sidebar column when there are two children (Sidebar + Main) */
      if (props.$hasSidebar) return '240px 1fr';
      return '1fr';
    }};
  }
`;

const StyledModalSidebar = styled.div`
  padding: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 768px) {
    padding: 16px;
    border-bottom: none;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    overflow-x: visible;
    overflow-y: auto;
    min-width: unset;
  }
`;


const StyledModalMainContent = styled.div`
  padding: 16px;
  overflow-y: auto;
  max-height: calc(80vh - 60px); /* Account for header height */
  -webkit-overflow-scrolling: touch;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.muted};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.mutedForeground};
  }

  @media (max-width: 767px) {
    max-height: calc(100vh - 12px - 52px);
    min-height: 0;
    padding: 12px 12px 100px 12px;
  }
  
  @media (min-width: 640px) {
    padding: 24px;
  }
`;

// Types
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  zIndex?: number;
  showCloseButton?: boolean;
}

interface ModalSidebarProps {
  children: React.ReactNode;
}

interface ModalContentProps {
  children: React.ReactNode;
}

interface ModalMainContentProps {
  children: React.ReactNode;
}

// Components
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size,
  zIndex,
  showCloseButton = true,
}) => {
  // When there are two children (ModalSidebar + ModalMainContent), use sidebar layout; otherwise full width
  const childCount = React.Children.count(children);
  const hasSidebar = size === 'lg' || size === 'xl' ? childCount === 2 : false;

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <ModalOverlay $isOpen={isOpen} $zIndex={zIndex} onClick={onClose}>
      <ModalContainer $isOpen={isOpen} $size={size} $zIndex={zIndex}>
        <ModalDialog $isOpen={isOpen} $size={size} onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{title}</ModalTitle>
            {showCloseButton && (
              <CloseButton onClick={onClose}>
                <X size={20} />
              </CloseButton>
            )}
          </ModalHeader>
          <ModalBody $size={size} $hasSidebar={hasSidebar}>
            {children}
          </ModalBody>
        </ModalDialog>
      </ModalContainer>
    </ModalOverlay>
  );

  // Use portal to render at document root level to avoid positioning issues
  return createPortal(modalContent, document.body);
};

export const ModalContent: React.FC<ModalContentProps> = ({ children }) => {
  return <>{children}</>;
};

export const ModalSidebar: React.FC<ModalSidebarProps> = ({ children }) => {
  return <StyledModalSidebar>{children}</StyledModalSidebar>;
};

export const ModalMainContent: React.FC<ModalMainContentProps> = ({ children }) => {
  return <StyledModalMainContent>{children}</StyledModalMainContent>;
};